import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Cell } from 'recharts'
import { ChartContainer } from '../../../../../common/chart/chartContainer'
import Bar from '../../../../../common/chart/chartContainer/Bar'
import { Legend } from '../../../../../common/chart/legend'
import { PieChartContainer } from '../../../../../common/chart/pieChart'
import {
  selectChartData,
  setChartData,
} from '../../../../../common/chart/store'
import { Element } from '../../../../../common/html/element'
import { SCREEN } from '../../../../../common/selectCurrencyUnit/constant'
import { useFormatCurrency } from '../../../../../common/selectCurrencyUnit/useFormatCurrency'
import { Tabs } from '../../../../../common/tabs'
import { getAppendStyleItemWithCustom } from '../../../../../common/tabs/helper'
import EventEmitter, {
  DID_MOUNT_COMPONENT,
} from '../../../../../helpers/EventEmitter'
import { getUnique, keyBy } from '../../../../../helpers/common'
import { formatByTheme } from '../../../../../helpers/theme'
import { formatVal, valToPercent } from '../../../../../helpers/value'
import { EMPTY_VALUE } from '../../../../../utils/common'
import {
  getNoDataComponent,
  getStyleHeightContainer,
  getYearTabs,
  handleIconDownload,
} from '../../helper'
import classes from '../../index.module.scss'
import { changeYear, selectYear } from '../../store/slice'
import { EACH_YEAR } from './constant'
import {
  selectByIdTable,
  selectDataViewByEachYear,
  selectIndicatorById,
  selectYears,
} from './store/slice'

const totalId = 'IS3'
const totalOperatingIncomeId = 'FA155'

const EachYear = ({
  id,
  isDynamicReport,
  year: yearSetting,
  isDownloadComponent,
  chart = [1, 2],
}) => {
  const ref = useRef()
  const pieChartId = useRef(getUnique())

  const chartId = pieChartId?.current

  const dispatch = useDispatch()
  const locale = useSelector((state) => state.i18n.locale)
  const year = useSelector(selectYear(id, yearSetting))
  const data = useSelector(selectDataViewByEachYear(year))
  const dataById = keyBy(data, 'id')
  const indicatorById = useSelector(selectIndicatorById)
  const dataByIdTable = useSelector(selectByIdTable)
  const years = useSelector(selectYears)

  const activeIndex = useSelector(
    selectChartData(chartId, (data) => data.activeIndex),
  )

  const isActive = (pieIndex) => {
    return (
      isNaN(activeIndex) || (!isNaN(activeIndex) && pieIndex === activeIndex)
    )
  }

  const { formatCurrencyWithUnit } = useFormatCurrency(
    SCREEN.FINANCIAL_ANALYSIS,
  )

  const tabs = getYearTabs(years)

  const gainFromProprietaryTradingText = indicatorById['FA065']?.ratioName

  const [size, setSize] = useState(0)

  useEffect(() => {
    calcSize()
    window.addEventListener('resize', calcSize)
    return () => window.removeEventListener('resize', calcSize)
  }, [year])

  const calcSize = () => {
    setSize(ref.current?.offsetWidth || 0)
  }

  const setYear = (year) => dispatch(changeYear({ id, year }))

  const onMouseOver = (pieIndex) => {
    if (chartId) {
      dispatch(setChartData({ chartId, activeIndex: pieIndex }))
    }
  }

  const onMouseLeave = () => {
    if (chartId) {
      dispatch(setChartData({ chartId, activeIndex: undefined }))
    }
  }

  useEffect(() => {
    if (
      (tabs.length && !year) ||
      !tabs.map((item) => item.key).includes(String(year))
    ) {
      setYear(tabs[tabs.length - 1]?.key)
    }
  }, [tabs.length, year])

  const barChartData = EACH_YEAR.OPERATING_INCOME_BREAKDOWN.map((item) => ({
    name: indicatorById[item.id]?.ratioName || '',
    value: dataById[item.id]?.value * 100,
    color: item.color,
  }))

  const pieChartData = EACH_YEAR.GAIN_PROPRIETARY_TRADING_BREAKDOWN.map(
    (item) => ({
      name: indicatorById[item.id]?.ratioName || '',
      value: dataById[item.id]?.value * 100,
      color: item.color,
    }),
  )

  const valueTotal = dataByIdTable[totalId]
    ? dataByIdTable[totalId]?.[year]
    : null

  const exchangeRateType = dataByIdTable[totalId]?.exchangeRateType

  const isNoData = !data.length

  useEffect(() => {
    handleIconDownload(id, isNoData)
  }, [isNoData])

  useEffect(() => {
    if (!chart.includes(1) && isDynamicReport) {
      EventEmitter.dispatch(DID_MOUNT_COMPONENT)
    }
  }, [])

  if (!year) return getNoDataComponent(isDynamicReport)

  const dataContainTotalOnly =
    data?.length === 1 && data[0]?.id === totalOperatingIncomeId

  const CustomizedLabel = (props) => {
    const locale = useSelector((state) => state.i18n.locale)
    let { x, y, value, width, height } = props

    y += isDynamicReport || isDownloadComponent ? 9 : 0
    x += (isDynamicReport || isDownloadComponent) && value >= 80 ? width / 2 : 0

    if (value >= 80) {
      return (
        <g style={{ pointerEvents: 'none' }}>
          <foreignObject
            x={x}
            y={y}
            height={height}
            width={width}
            color="#FFFFFF"
          >
            <Element
              className="h100 w100 dFlex alignItemsCenter justifyContentCenter"
              style={{ color: '#ffffff' }}
            >
              {valToPercent(
                value,
                locale,
                true,
                undefined,
                value === 100 ? 0 : 2,
              )}
            </Element>
          </foreignObject>
        </g>
      )
    }

    const valuePercent = valToPercent(value, locale, true)

    return (
      <g>
        <foreignObject
          x={x + (valuePercent !== EMPTY_VALUE ? width : 0) + 3}
          y={y}
          height={height}
          width={200}
        >
          <Element
            className="h100 w100 dFlex alignItemsCenter"
            style={{ color: '#3A414B' }}
          >
            {valuePercent}
          </Element>
        </foreignObject>
      </g>
    )
  }

  const isH100 = chart.includes(1) && chart.includes(2)

  return (
    <div
      style={{
        height: getStyleHeightContainer(
          isDynamicReport,
          isNoData,
          isH100 ? 700 : chart.includes(1) ? 400 : 300,
        ),
      }}
      className={`flexColumn ${classes.operatingIncomeEachYear}`}
    >
      <Element
        style={{
          borderBottom: !isDynamicReport ? '1px solid #B5CEF8' : undefined,
        }}
        className={`dFlex justifyContentEnd ${classes.tabContainer}`}
      >
        {!isDynamicReport ? (
          <Tabs
            activeKey={year}
            items={tabs}
            tabListClassName={classes.tabs}
            getAppendStyleItem={getAppendStyleItemWithCustom()}
            isScrollContent={false}
            onChange={setYear}
          />
        ) : (
          <>
            {!isNoData && (
              <div className="fw600 py1 w100">
                <Translate value="common.FISCAL_YEAR" />: {year}
              </div>
            )}
          </>
        )}
      </Element>
      {isNoData || dataContainTotalOnly ? (
        getNoDataComponent(isDynamicReport)
      ) : (
        <div
          className={
            !isDynamicReport
              ? 'h100 py1 dFlex'
              : 'h100 py1 flexColumn justifyContentBetween'
          }
        >
          {chart.includes(1) && (
            <div
              className={
                !isDynamicReport
                  ? 'flexColumn w50 h100 pr1'
                  : `flexColumn w100 ${isH100 ? 'h55' : 'h100'}`
              }
            >
              <div className="fw600 pb2">
                <Translate value="financialAnalysis.OPERATING_INCOME_BREAKDOWN" />
              </div>
              <ChartContainer
                data={barChartData}
                width="100%"
                height="100%"
                layout="vertical"
                margin={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                }}
                cartesianGrid={{
                  vertical: true,
                  horizontal: false,
                  stroke: formatByTheme('#E5E5E8'),
                }}
                yAxis={[
                  {
                    dataKey: 'name',
                    yAxisId: 'left',
                    type: 'category',
                    width: 200,
                    tickStyle: { color: '#3A414B' },
                    lineClamp: 2,
                  },
                ]}
                xAxis={[
                  {
                    type: 'number',
                    dataKeys: ['value'],
                    ticks: [0, 20, 40, 60, 80, 100],
                    axisLine: false,
                    hide: true,
                  },
                ]}
                tooltip={{
                  schema: {
                    title: {
                      hide: true,
                    },
                    content: [
                      {
                        dataKey: 'value',
                        formatValue: (value) =>
                          formatCurrencyWithUnit({
                            value: (value / 100) * valueTotal,
                            year,
                            exchangeRateType: exchangeRateType,
                          }),
                      },
                    ],
                  },
                }}
              >
                <Bar
                  yAxisId="left"
                  dataKey="value"
                  maxBarSize={35}
                  label={<CustomizedLabel />}
                >
                  {barChartData.map((item, index) => {
                    return <Cell key={`cell-${index}`} fill={item.color} />
                  })}
                </Bar>
              </ChartContainer>
            </div>
          )}
          {chart.includes(2) && (
            <div
              className={
                !isDynamicReport
                  ? 'w50 h100 pl1 flexColumn'
                  : `w100 ${isH100 ? 'h40' : 'h100'} flexColumn`
              }
            >
              <div className="fw600">
                <Translate value="financialAnalysis.GAIN_PROPRIETARY_TRADING_BREAKDOWN" />
              </div>
              <div className="h100 dFlex">
                <div className="flexColumn w60 h100">
                  <div
                    ref={ref}
                    className="h100 w100 px1 py1 dFlex alignItemsCenter justifyContentCenter"
                  >
                    <PieChartContainer
                      chartId={chartId}
                      data={pieChartData}
                      width={size}
                      height={195}
                      innerRadius="60%"
                      labelCenter={{
                        customLabel: () => gainFromProprietaryTradingText,
                        outerRadius: '50%',
                        fill: '#FFCD60',
                        style: {
                          fontSize: 12,
                          color: formatByTheme('#3A414B'),
                        },
                      }}
                      isLabelActive={false}
                      formatValueTooltip={(val) => formatVal(val, locale) + '%'}
                    />
                  </div>
                  <div
                    className={`dFlex py1 ${classes.gap6} justifyContentCenter`}
                  >
                    {pieChartData.map((item, index) => {
                      return (
                        <Element
                          style={{
                            backgroundColor: item.color,
                            color: '#FFFFFF',
                            opacity: isActive(index) ? 1 : 0.4,
                          }}
                          key={index}
                          className={classes.percentItem}
                          onMouseOver={() => onMouseOver(index)}
                          onMouseLeave={() => onMouseLeave}
                        >
                          {formatVal(item.value, locale)}%
                        </Element>
                      )
                    })}
                  </div>
                </div>
                <div className="h100 w40 pl1 dFlex alignItemsCenter">
                  <Legend
                    chartId={chartId}
                    items={pieChartData.map((item, index) => ({
                      ...item,
                      title: item.name,
                      pieIndex: index,
                      isI18n: false,
                    }))}
                    className="flexColumn"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default EachYear
